/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {finalize} from 'rxjs';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import * as _ from 'lodash';

import template from './invoice-translation-edit-page.component.html';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LOCALES} from '../common/constants/locales';
import {SignalAdminResourceEditPageBase} from '../common/controller/signal-admin-resource-edit-page-base';
import {
  getInvoicePaymentProvider,
  getInvoiceTranslationKeys,
  initInvoiceTranslationModel,
  isInvoicePreviewSupported,
  normalizeInvoiceLogoUrl,
  prepareInvoiceTranslationForSubmit,
  updateInvoiceTranslationInfo
} from './invoice-translation-commons';
import {InvoiceTranslationApiService, InvoiceTranslationModel} from './invoice-translation-api.service';
import {APP_SHARED} from '../common/app-shared';
import {CodemirrorFieldComponent} from '../common/directives/codemirror-field.component';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TranslateModule, ButtonsModule, CodemirrorFieldComponent],
  selector: 'adn-invoice-translation-edit-page',
  template
})
export class InvoiceTranslationEditPageComponent extends SignalAdminResourceEditPageBase<InvoiceTranslationModel> {
  readonly locales = LOCALES;
  readonly defaultTranslations = signal<Record<string, any>>({});
  readonly translationKeys = signal<string[]>([]);
  readonly doingPreview = signal(false);
  readonly previewHtml = signal<SafeHtml | null>(null);
  readonly notValid: boolean;
  readonly paymentProvider: string;
  transDir = false;

  private readonly invoiceApi: InvoiceTranslationApiService;
  private readonly sanitizer = inject(DomSanitizer);
  private readonly invoiceDestroyRef = inject(DestroyRef);

  constructor() {
    const api = inject(InvoiceTranslationApiService);
    super(api, 'invoiceTranslation');
    this.invoiceApi = api;
    const networkProfile = inject(LocalNetworkProfile);
    this.paymentProvider = getInvoicePaymentProvider(networkProfile);
    this.notValid = !isInvoicePreviewSupported(this.paymentProvider);
    this.loadDefaultTranslations();
  }

  updateTranslations(): void {
    this.editingModel.translations = _.cloneDeep(this.defaultTranslations());
    this.markExternalFormDirty();
  }

  onLogoUrlBlur(): void {
    this.editingModel.logoUrl = normalizeInvoiceLogoUrl(this.editingModel.logoUrl);
  }

  protected override initialiseModel(model: InvoiceTranslationModel): InvoiceTranslationModel {
    const initialised = super.initialiseModel(model);
    initInvoiceTranslationModel(initialised, this.defaultTranslations());
    this.loadPreview(initialised);
    return initialised;
  }

  protected override afterSave(model: InvoiceTranslationModel): void {
    updateInvoiceTranslationInfo(model, this.defaultTranslations());
    this.loadPreview(model);
  }

  protected override prepareForSubmit(model: InvoiceTranslationModel): void {
    prepareInvoiceTranslationForSubmit(model, this.defaultTranslations());
  }

  private loadDefaultTranslations(): void {
    this.invoiceApi.getDefaultTranslations().pipe(
      takeUntilDestroyed(this.invoiceDestroyRef)
    ).subscribe({
      next: (defaults) => {
        const translations = defaults || {};
        this.defaultTranslations.set(translations);
        this.translationKeys.set(getInvoiceTranslationKeys(translations));
        initInvoiceTranslationModel(this.editingModel, translations);
      },
      error: () => undefined
    });
  }

  private loadPreview(model: InvoiceTranslationModel): void {
    if (this.state?.isNew() || !model?.id) {
      this.previewHtml.set(null);
      return;
    }
    this.doingPreview.set(true);
    this.invoiceApi.getPreview(model.id, this.paymentProvider).pipe(
      finalize(() => this.doingPreview.set(false)),
      takeUntilDestroyed(this.invoiceDestroyRef)
    ).subscribe({
      next: (preview) => this.previewHtml.set(
        this.sanitizer.bypassSecurityTrustHtml(preview.html)
      ),
      error: () => this.previewHtml.set(null)
    });
  }
}
